import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';

import {
	selectIsAuthenticated,
	selectProduct,
	selectTheme,
	selectUUID,
} from 'stash/selectors';
import useGraphQLData from './hooks/useGraphQLData';
import useAuthRedirect from './hooks/useAuthRedirect';
import useAttributionOnUserCreate from './hooks/useAttributionOnUserCreate';

import {
	getAdvisorySignature,
	getDebitAccount,
	getDynamicPlans,
	getManageExternalAccount,
	getPlans,
	getQuestions,
	getUserSubscription,
	postUserTestAssignments,
} from 'stash/actions/api';

import { getCookie } from 'stash/utils/cookies';

import AuthenticatedOnboardingAppUI from './AuthenticatedOnboardingAppUI';
import { IDV_PX_QUESTION_NAME } from 'stash/constants/identityVerification';
import { useFetch } from '@stashinvest/use-fetch';
import { WEB_APP_PATH } from 'stash/constants';
import {
	STATE_ZERO_OID,
	pxQuestionsAnswerTypes,
} from 'stash/components/screens/state-zero-home/constants';
import { LoaderIndeterminateCircular } from '@stashinvest/react-components';
import {
	EXPERIMENTS,
	useCurrentExperimentVariant,
	useIsInExperimentVariation,
	USER_SIGN_UP_VARIATIONS,
} from 'stash/components/experiments';
import { v1_Experiment_Viewed } from '@stashinvest/product-events-javascript';
import { useTrack } from 'stash/hooks/useTrack';

const UniversalOnboardingApp = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const [isContainerReady, setIsContainerReady] = useState(false);
	const [isRestDataReady, setIsRestDataReady] = useState(false);

	const isAuthenticated = useSelector(selectIsAuthenticated);
	const userUuid = useSelector(selectUUID);
	const theme = useSelector(selectTheme);
	const product = useSelector(selectProduct);
	const isDynamicTiers = useIsInExperimentVariation(
		EXPERIMENTS.IS_DYNAMIC_TIERS_ENABLED,
		true
	);
	const { fetch: getLaunchInfo } = useFetch({
		method: useFetch.GET,
		path: `/apis/onboarding/api/v2/users/:uuid/launch-info`,
	});
	const track = useTrack();

	const { authRedirect } = useAuthRedirect();
	const checkUserCompleteWithStateZeroStatus = async () => {
		const { location } = await getLaunchInfo();
		const externalBankAndPxQuestionsResult = await Promise.allSettled([
			dispatch(getManageExternalAccount()),
			dispatch(getQuestions(STATE_ZERO_OID)),
		]);

		externalBankAndPxQuestionsResult.forEach((result) => {
			if (result.status === 'rejected') {
				console.error(
					'Registration Container external Bank & Px Questions',
					result.reason
				);
			}
		});

		const externalBankAccount =
			externalBankAndPxQuestionsResult[0]?.value?.manageExternalBankAccount?.bankAccount;
		const stateZeroPxQuestionValue =
			externalBankAndPxQuestionsResult[1]?.value?.question?.answer?.value;
		return (
			location === 'HOME' &&
			(Boolean(externalBankAccount) ||
				(stateZeroPxQuestionValue !== pxQuestionsAnswerTypes.PERSONAL_BROKERAGE_ACCOUNT &&
					stateZeroPxQuestionValue !==
						pxQuestionsAnswerTypes.ROBO_PERSONAL_BROKERAGE_ACCOUNT))
		);
	};

	const variant = useCurrentExperimentVariant(EXPERIMENTS.USER_SIGN_UP_MULTI_VARIANT);
	useEffect(() => {
		if (!isAuthenticated && authRedirect) {
			if (
				getCookie('hasSeenWelcomeCarousel') !== 'true' &&
				variant === USER_SIGN_UP_VARIATIONS.SHOW_WELCOME_CAROUSEL_SCREEN
			) {
				track(
					v1_Experiment_Viewed({
						experiment: EXPERIMENTS.USER_SIGN_UP_MULTI_VARIANT,
						variant: variant,
					})
				);
				// go to carousel
				const redirectTo = `/${window.location.pathname.split('/')[1]}/welcome${
					window.location.search
				}`;
				return history.push(redirectTo);
			} else if (variant === USER_SIGN_UP_VARIATIONS.SHOW_INTENT_QUESTION_SCREEN) {
				track(
					v1_Experiment_Viewed({
						experiment: EXPERIMENTS.USER_SIGN_UP_MULTI_VARIANT,
						variant: variant,
					})
				);
				// go to intent question
				const redirectTo = `/${window.location.pathname.split('/')[1]}/intent${
					window.location.search
				}`;
				return history.push(redirectTo);
			} else {
				track(
					v1_Experiment_Viewed({
						experiment: EXPERIMENTS.USER_SIGN_UP_MULTI_VARIANT,
						variant: variant,
					})
				);

				setTimeout(() => {
					window.location.replace(authRedirect);
				}, 500);
			}
		}
	}, [isAuthenticated, authRedirect, history]);

	const { isGraphQLDataReady, userResponse } = useGraphQLData();

	useAttributionOnUserCreate();

	useEffect(() => {
		if (isAuthenticated && userUuid) {
			datadogRum.setUser({ id: userUuid });
		} else {
			datadogRum.setUser({ id: null });
		}
	}, [isAuthenticated, userUuid]);

	useEffect(() => {
		const isReadyToFetch = isAuthenticated && !!userResponse?.userWithAccounts;

		const fetchRestData = async () => {
			if (isReadyToFetch) {
				try {
					if (await checkUserCompleteWithStateZeroStatus()) {
						window.location.href = WEB_APP_PATH;
					} else {
						const results = await Promise.allSettled([
							dispatch(getPlans()),
							isDynamicTiers ? dispatch(getDynamicPlans()) : Promise.resolve(),
							dispatch(getUserSubscription()), // TODO: Move to graphql once all new users move to new subscription service.
							dispatch(getAdvisorySignature()), // TODO: Move to graphql once all new users move to new subscription service.
							dispatch(getDebitAccount()),
							dispatch(postUserTestAssignments()),
							dispatch(getQuestions(IDV_PX_QUESTION_NAME)),
						]);
						results.forEach((result) => {
							if (result.status === 'rejected') {
								console.error('Registration Container', result.reason);
							}
						});

						setIsRestDataReady(true);
					}
				} catch (error) {
					console.error('UniversalOnboardingApp:Promise.allSettled', error);
				}
			}
		};

		fetchRestData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userResponse, dispatch, isAuthenticated, getLaunchInfo]);

	useEffect(() => {
		if (isAuthenticated && isGraphQLDataReady && isRestDataReady) {
			setIsContainerReady(true);
		}
	}, [isAuthenticated, isGraphQLDataReady, isRestDataReady]);

	return isContainerReady ? (
		<AuthenticatedOnboardingAppUI
			ready={isContainerReady}
			uuid={userUuid}
			theme={theme}
			product={product}
		/>
	) : (
		<div className="flex items-center justify-center h-screen">
			<LoaderIndeterminateCircular
				size="large"
				// eslint-disable-next-line react/style-prop-object
				style="solid"
				fill="accentFixed"
			/>
		</div>
	);
};

export default UniversalOnboardingApp;
